/**
 * AuthContext - Contexto de autenticação
 * Autenticação via Firebase (Google OAuth) e sincronização de usuários
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../config/firebase";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { useUserSync } from "../hooks/useUserSync";
import LoadingSpinner from "../components/LoadingSpinner";

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [churchUser, setChurchUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Hooks de autenticação
  const firebaseAuth = useFirebaseAuth();
  const userSync = useUserSync();

  const clearAuthError = useCallback(() => setAuthError(null), []);

  // Função de logout que limpa os estados
  const logout = async () => {
    try {
      await firebaseAuth.logout();
      setCurrentUser(null);
      setChurchUser(null);
    } catch (error) {
      // Mesmo com erro, limpar estados
      setCurrentUser(null);
      setChurchUser(null);
      throw error;
    }
  };

  // Efeito para monitorar estado de autenticação Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Sinalizar loading enquanto sincroniza dados do membro
        setLoading(true);
        setCurrentUser(user);

        try {
          // Carregar ou criar dados do usuário da igreja
          const churchUserData = await userSync.createOrUpdateChurchUser(user);

          // Garantir que o campo musico sempre exista
          if (churchUserData.musico === undefined) {
            churchUserData.musico = false;
          }

          console.log("[AuthContext] churchUser carregado:", { uid: user.uid, role: churchUserData.role, membro_id: churchUserData.membro_id });
          setChurchUser(churchUserData);

          // Salvar dados do usuário no localStorage
          userSync.salvarDadosLocalStorage(user, churchUserData);
        } catch (error) {
          console.error("Erro ao carregar dados do usuário:", error);

          if (error?.code === "auth/email-nao-cadastrado") {
            // Email não está na coleção membros - já foi feito signOut no hook
            setCurrentUser(null);
            setChurchUser(null);
            setAuthError(error.message);
          } else {
            setChurchUser(null);
          }
        }
      } else {
        setCurrentUser(null);
        setChurchUser(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Valor do contexto
  const value = {
    // Estado
    currentUser,
    churchUser,
    loading,
    authError,

    // Métodos Firebase Auth
    loginWithGoogle: firebaseAuth.loginWithGoogle,
    logout,
    clearAuthError,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <LoadingSpinner
          fullPage={true}
          size="lg"
          message="Inicializando aplicação..."
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
